import { useRef, useState } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'
import Link from 'next/link'

import Container from '@/components/base/container'
import Grid from '@/components/base/grid'
import Motion from '@/components/base/motion'
import SVGIcon from '@/components/base/svg-icon'
import Typography from '@/components/base/typography'
import { quickCollectionMenuRevealAnimation } from '@/components/sections/animations/quick-collection-menu'
import Spacer from '@/components/sections/spacer'

import { cn } from '@/lib/utils'

type QuickCollectionMenuProps = {
  collection: any[]
}

const QuickCollectionMenu = ({
  collection,
  ...props
}: QuickCollectionMenuProps) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const [active, setActive] = useState(0)

  useGSAP(
    () => {
      const root = rootRef.current
      if (!root) return

      const item = root.querySelectorAll(`[data-motion-item]`)[0]
      const image = root.querySelector('[data-collection-image]')
      const wrapper = root.querySelector('[data-motion-images]')
      const firstImage = root.querySelector('[data-motion-image]')
      if (!wrapper || !firstImage) return

      const imageHeight = firstImage.clientHeight

      gsap.to(image, {
        y: item.clientHeight * active
      })

      gsap.to(wrapper, {
        y: -imageHeight * active,
        ease: 'power3.out',
        duration: 0.8
      })
    },
    {
      scope: rootRef,
      dependencies: [active]
    }
  )

  return (
    <Motion animation={quickCollectionMenuRevealAnimation}>
      {(motionRef) => (
        <section ref={motionRef} {...props}>
          <Container ref={rootRef}>
            <Spacer variant="xxl" />
            <Grid>
              <div className="col-span-2 md:col-span-4 md:col-start-3 lg:col-span-7 lg:col-start-6">
                <div className="relative flex flex-col">
                  <div
                    className="md:[29vw] absolute left-0 top-0 hidden aspect-[1.75] translate-x-[calc(-100%-24px)] overflow-hidden md:block lg:w-[350px]"
                    data-collection-image
                  >
                    <div data-motion-images>
                      {collection?.length &&
                        collection.map((item, index) => (
                          <div
                            key={`image-${index}`}
                            className="aspect-[1.75]"
                            data-motion-image
                          >
                            {item.image?.url && (
                              <img
                                src={item.image.sizes?.medium?.url}
                                alt={item.image.alt || ''}
                                className="h-full w-full object-cover object-center"
                              />
                            )}
                          </div>
                        ))}
                    </div>
                  </div>
                  {collection?.length &&
                    collection.map((item, index) => (
                      <Link
                        href={item.link}
                        key={`item-${index}`}
                        scroll={false}
                        className={cn(
                          'group relative flex items-center justify-between py-spacing-xs',
                          {
                            active: active === index
                          }
                        )}
                        data-motion-item
                        onPointerEnter={() => setActive(index)}
                      >
                        <div className="flex">
                          <Typography
                            className={cn(
                              'opacity-100 transition-opacity duration-300 ease-out-cubic md:opacity-50',
                              {
                                'md:opacity-100': active === index
                              }
                            )}
                            variant="h6"
                          >
                            {item.title}
                          </Typography>
                        </div>
                        <div className="flex items-center gap-[10px]">
                          {/* <div className="flex items-center rounded-radius-minimal bg-surface-primay-invert-dark p-spacing-2xs text-text-heading-invert-light dark:bg-surface-primay-invert-light dark:text-text-primary-invert-dark">
                            <Typography variant="regular" className="">
                              {item.items.padStart(2, '0')}
                            </Typography>
                          </div> */}
                          <SVGIcon
                            symbol="cta-arrow"
                            className={cn(
                              'size-[24px]',
                              'transition-transform duration-300 ease-out-cubic',
                              { 'md:rotate-45': active === index }
                            )}
                            aria-hidden="true"
                          />
                        </div>
                        <div className="absolute bottom-0 h-[1px] w-full bg-[rgba(0,0,0,0.25)] dark:bg-[rgba(255,255,255,0.25)]" />
                        <div
                          className={cn(
                            'absolute bottom-0 hidden h-[1px] w-full bg-[rgba(0,0,0,0.25)] md:block dark:bg-[rgba(255,255,255,0.25)]',
                            'origin-left scale-x-0 transition-transform duration-500 ease-in-out-cubic',
                            { 'scale-x-100 ease-out-cubic': active === index }
                          )}
                        />
                      </Link>
                    ))}
                </div>
              </div>
            </Grid>
            <Spacer variant="xxl" />
          </Container>
        </section>
      )}
    </Motion>
  )
}

export default QuickCollectionMenu
